/**
 * Error utilities for consistent error handling across the application
 */

export enum ErrorType {
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  NETWORK = 'NETWORK',
  VALIDATION = 'VALIDATION',
  NOT_FOUND = 'NOT_FOUND',
  SERVER = 'SERVER',
  UNKNOWN = 'UNKNOWN',
}

export interface AppError {
  type: ErrorType;
  message: string;
  details?: string;
  statusCode?: number;
  timestamp: Date;
}

/**
 * Categorize errors based on status code and error content
 */
export function categorizeError(error: unknown): AppError {
  const timestamp = new Date();

  // Handle errors with status or statusCode property
  if (error && typeof error === 'object' && ('status' in error || 'statusCode' in error)) {
    const errorObj = error as {
      statusCode?: number;
      status?: number;
      message?: string;
      detail?: string;
      type?: string;
    };
    const statusCode = errorObj.statusCode || errorObj.status;
    const errorMessage = errorObj.message || errorObj.detail;

    switch (statusCode) {
      case 401:
        // ApiClient deliberately throws a post-refresh 401 as an
        // ApiError with type 'AuthorizationError' (the session was just
        // refreshed, so the 401 is a permission problem, not a dead
        // session). Respect that so AuthErrorBoundary does not force a
        // logout — only authentication-origin 401s should.
        if (errorObj.type === 'AuthorizationError') {
          return {
            type: ErrorType.AUTHORIZATION,
            message: errorMessage || "You don't have permission to perform this action.",
            statusCode,
            timestamp,
          };
        }
        return {
          type: ErrorType.AUTHENTICATION,
          message: errorMessage || 'Your session has expired. Please sign in again.',
          statusCode,
          timestamp,
        };

      case 403:
        return {
          type: ErrorType.AUTHORIZATION,
          message: errorMessage || "You don't have permission to perform this action.",
          statusCode,
          timestamp,
        };

      case 404:
        return {
          type: ErrorType.NOT_FOUND,
          message: errorMessage || 'The requested resource was not found.',
          statusCode,
          timestamp,
        };

      case 422:
        return {
          type: ErrorType.VALIDATION,
          message: errorMessage || 'The submitted data is invalid. Please check your input.',
          statusCode,
          timestamp,
        };

      case 429:
        return {
          type: ErrorType.SERVER, // Rate limiting is a server-side concern
          message: errorMessage || 'Too many requests. Please slow down and try again.',
          statusCode,
          timestamp,
        };

      case 500:
      case 502:
      case 503:
      case 504:
        return {
          type: ErrorType.SERVER,
          message: errorMessage || 'A server error occurred. Please try again later.',
          statusCode,
          timestamp,
        };

      default:
        if (statusCode && statusCode >= 400 && statusCode < 500) {
          return {
            type: ErrorType.VALIDATION,
            message: errorMessage || 'There was a problem with your request.',
            statusCode,
            timestamp,
          };
        } else if (statusCode && statusCode >= 500) {
          return {
            type: ErrorType.SERVER,
            message: errorMessage || 'An unexpected server error occurred.',
            statusCode,
            timestamp,
          };
        }
    }
  }

  // Handle network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return {
      type: ErrorType.NETWORK,
      message: 'Unable to connect to the server. Please check your internet connection.',
      timestamp,
    };
  }

  // Handle Error instances
  if (error instanceof Error) {
    // Check if error has statusCode property (from our API client)
    const errorWithStatus = error as Error & {
      statusCode?: number;
      detail?: unknown;
      type?: string;
    };
    if (errorWithStatus.statusCode) {
      return categorizeError({
        statusCode: errorWithStatus.statusCode,
        message: error.message,
        detail: errorWithStatus.detail,
        type: errorWithStatus.type,
      });
    }

    // Check for specific error messages
    if (error.message.toLowerCase().includes('network')) {
      return {
        type: ErrorType.NETWORK,
        message: 'A network error occurred. Please check your connection.',
        details: error.message,
        timestamp,
      };
    }

    if (
      error.message.toLowerCase().includes('unauthorized') ||
      error.message.toLowerCase().includes('authentication')
    ) {
      return {
        type: ErrorType.AUTHENTICATION,
        message: 'Authentication failed. Please sign in again.',
        details: error.message,
        timestamp,
      };
    }

    if (
      error.message.toLowerCase().includes('forbidden') ||
      error.message.toLowerCase().includes('permission')
    ) {
      return {
        type: ErrorType.AUTHORIZATION,
        message: "You don't have permission to access this resource.",
        details: error.message,
        timestamp,
      };
    }

    // Generic error with message
    return {
      type: ErrorType.UNKNOWN,
      message: 'An unexpected error occurred.',
      details: error.message,
      timestamp,
    };
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      type: ErrorType.UNKNOWN,
      message: error,
      timestamp,
    };
  }

  // Fallback for unknown errors
  return {
    type: ErrorType.UNKNOWN,
    message: 'An unexpected error occurred. Please try again.',
    timestamp,
  };
}

/**
 * Get user-friendly error message based on error type
 */
export function getUserFriendlyMessage(error: AppError): string {
  switch (error.type) {
    case ErrorType.AUTHENTICATION:
      return error.message || 'Please sign in to continue.';

    case ErrorType.AUTHORIZATION:
      return error.message || "You don't have permission to perform this action.";

    case ErrorType.NETWORK:
      return error.message || 'Connection error. Please check your internet connection.';

    case ErrorType.VALIDATION:
      return error.message || 'Please check your input and try again.';

    case ErrorType.NOT_FOUND:
      return error.message || 'The requested item could not be found.';

    case ErrorType.SERVER:
      return error.message || 'Server error. Please try again later.';

    default:
      return error.message || 'Something went wrong. Please try again.';
  }
}

/**
 * Check if error is recoverable (user can retry)
 */
export function isRecoverableError(error: AppError): boolean {
  return [ErrorType.NETWORK, ErrorType.SERVER, ErrorType.UNKNOWN].includes(error.type);
}

/**
 * Check if error requires re-authentication
 */
export function requiresAuthentication(error: AppError): boolean {
  return error.type === ErrorType.AUTHENTICATION;
}

/**
 * Format error for logging (includes details, excludes sensitive info)
 */
export function formatErrorForLogging(error: AppError): string {
  const parts = [`[${error.type}]`, error.message];

  if (error.statusCode) {
    parts.push(`(HTTP ${error.statusCode})`);
  }

  if (error.details && import.meta.env.DEV) {
    parts.push(`- ${error.details}`);
  }

  return parts.join(' ');
}

/**
 * Get error type from any error
 */
export function getErrorType(error: unknown): ErrorType {
  const appError = categorizeError(error);
  return appError.type;
}

/**
 * Get error message from any error
 */
export function getErrorMessage(error: unknown): string {
  const appError = categorizeError(error);
  return getUserFriendlyMessage(appError);
}
